
 
import React, { useState, forwardRef, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, subDays, differenceInDays } from 'date-fns';
import { FaRegCalendarAlt, FaDownload } from 'react-icons/fa';
import { ManText, ManCard, ManButton } from '@craft-design-system/core-components-react';
import axios from 'axios';
import { useGraphContext } from '../../GraphContext';
import './RideDataDownload.css'; // Custom styles for date picker
 
const RideDataDownload = () => {
  const { graphData } = useGraphContext();
  const { vin_short } = graphData || {};
 
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [downloadInfo, setDownloadInfo] = useState(null);
  const [showVehicles, setShowVehicles] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
 
  // Quick date range presets
  const setDateRange = (days) => {
    const end = new Date();
    const start = subDays(end, days - 1);
    setStartDate(start);
    setEndDate(end);
  };
 
  // Calculate date range duration
  const daysDifference = useMemo(() => {
    if (startDate && endDate) {
      return differenceInDays(endDate, startDate) + 1;
    }
    return 0;
  }, [startDate, endDate]);
 
  // Custom input for date picker with calendar icon
  const CustomDateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
    <div
      onClick={onClick}
      ref={ref}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: 'pointer',
        padding: '10px 14px',
        border: '2px solid #dee2e6',
        borderRadius: '6px',
        backgroundColor: 'white',
        minWidth: '180px',
        transition: 'all 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.borderColor = '#303C49';
        e.currentTarget.style.boxShadow = '0 2px 6px rgba(48,60,73,0.15)';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.borderColor = '#dee2e6';
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.05)';
      }}
    >
      <FaRegCalendarAlt size={18} color="#303C49" />
      <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057' }}>
        {value || placeholder}
      </span>
    </div>
  ));
 
  const handleDownload = async () => {
    if (!vin_short || vin_short.length === 0) {
      setError('Please select at least one vehicle first');
      return;
    }
 
    if (startDate > endDate) {
      setError('Start date cannot be after end date');
      return;
    }
 
    setIsDownloading(true);
    setError(null);
    setDownloadInfo(null);
 
    // Determine baseURL based on hostname (moved outside try block for error logging)
    let baseURL;
    if (window.location.hostname === "simbaanalytics-dev.rio.cloud") {
      baseURL = "https://26ovsbph5oz6xpn2fgscmr5x5i0socao.lambda-url.eu-west-1.on.aws";
    } else if (window.location.hostname === "bev-dev.simbaanalytics-dev.rio.cloud") {
      baseURL = "https://spv3toxlwj.execute-api.eu-west-1.amazonaws.com/test_cognito";
    } else if (window.location.hostname === "bev-prod.simbaanalytics-dev.rio.cloud") {
      baseURL = "https://dnz0zln7l2.execute-api.eu-west-1.amazonaws.com/prod";
    } else {
      baseURL = "http://localhost:8080";
    }
 
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');
 
      // Log download attempt
      console.log('🔄 Starting file download:', {
        vins: vin_short,
        dateRange: `${formattedStartDate} to ${formattedEndDate}`,
        environment: window.location.hostname,
        timestamp: new Date().toISOString()
      });
 
      const token = localStorage.getItem("idToken");
      if (!token) {
        setError('Authentication required. Please log in again.');
        setIsDownloading(false);
        return;
      }
 
      const response = await axios.post(
        `${baseURL}/download-ride-data`,
        {
          vin_short: vin_short,
          start_date: formattedStartDate,
          end_date: formattedEndDate,
          database: 'ufdb',
        },
        {
          responseType: 'json',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
 
      // Handle JSON response with S3 download URL
      const downloadInfo = response.data;
     
      // Log comprehensive download information
      console.log('📊 S3 Download Info:', {
        fileSize: `${downloadInfo.file_size_mb} MB (${downloadInfo.file_size.toLocaleString()} bytes)`,
        compressed: downloadInfo.compressed,
        compressionRatio: downloadInfo.compressed ? `${downloadInfo.compression_ratio}%` : 'N/A',
        bandwidthSaved: downloadInfo.compressed ? `${downloadInfo.bandwidth_saved_kb} KB` : 'N/A',
        rowCount: downloadInfo.row_count,
        vinCount: downloadInfo.vin_count,
        dateRange: downloadInfo.date_range,
        filename: downloadInfo.filename,
        expiresAt: new Date(downloadInfo.expires_at).toLocaleString(),
        expiresInMinutes: Math.round(downloadInfo.expires_in_seconds / 60),
        storageInfo: downloadInfo.storage_info,
        timestamp: new Date().toISOString()
      });
 
      // Validate download URL
      if (!downloadInfo.download_url) {
        throw new Error('No download URL received from server');
      }
 
      // Download file from S3 using the presigned URL
      console.log('🔗 Initiating download from S3 URL');
     
      // Open the presigned URL in a new window/tab to trigger download
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadInfo.download_url;
      downloadLink.download = downloadInfo.filename;
      downloadLink.target = '_blank'; // Open in new tab as backup
      downloadLink.rel = 'noopener noreferrer';
     
      // Add link to DOM temporarily
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
 
      // Log successful completion
      console.log('✅ Download initiated successfully:', {
        filename: downloadInfo.filename,
        fileSize: `${downloadInfo.file_size_mb} MB`,
        compressionUsed: downloadInfo.compressed,
        urlExpiresAt: new Date(downloadInfo.expires_at).toLocaleString(),
        downloadMethod: 'S3 Presigned URL',
        timestamp: new Date().toISOString()
      });
 
      // Store download info for UI display
      setDownloadInfo(downloadInfo);
 
      // Show success message with file details
      if (downloadInfo.compressed) {
        console.log('🗜️ File compression saved ' + downloadInfo.bandwidth_saved_kb + ' KB bandwidth (' + downloadInfo.compression_ratio + '% reduction)');
      }
    } catch (err) {
      // Enhanced error logging for S3 download issues
      console.error('❌ S3 Download failed:', {
        error: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data,
        contentType: err.response?.headers?.['content-type'],
        url: baseURL,
        timestamp: new Date().toISOString()
      });
 
      // Handle different types of errors
      if (err.response?.status === 404) {
        setError('No data found for the selected date range and vehicles');
      } else if (err.response?.status === 400) {
        // Try to extract more specific error from response
        const errorDetail = err.response?.data?.detail || 'Invalid date range or parameters selected';
        setError(errorDetail);
      } else if (err.response?.status === 500) {
        // Server error - could be S3 issue, compression failure, etc.
        const errorDetail = err.response?.data?.detail || 'Server error occurred while preparing your download';
        setError(errorDetail + '. Please try again or contact support if the issue persists.');
      } else if (err.response?.status === 403) {
        setError('Access denied. Please check your permissions or log in again.');
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        setError('Network error. Please check your connection and try again.');
      } else if (err.message.includes('No download URL')) {
        setError('Failed to generate download link. Please try again or contact support.');
      } else {
        // Generic error fallback
        setError('Failed to prepare download. Please try again or contact support if the issue persists.');
      }
     
      // Log specific error patterns for debugging
      if (err.response?.data && typeof err.response.data === 'object') {
        console.warn('🔍 Structured error response:', err.response.data);
      } else if (err.response?.data && typeof err.response.data === 'string' && err.response.data.includes('<html>')) {
        console.warn('🔴 HTML error page returned - likely server configuration issue');
      }
    } finally {
      setIsDownloading(false);
    }
  };
 
  return (
    <ManCard
      style={{
        margin: '20px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}
    >
      {/* Collapsed Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '15px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          backgroundColor: isExpanded ? '#f8f9fa' : 'white',
          transition: 'background-color 0.2s',
          borderBottom: isExpanded ? '1px solid #dee2e6' : 'none',
        }}
        onMouseOver={(e) => {
          if (!isExpanded) e.currentTarget.style.backgroundColor = '#f8f9fa';
        }}
        onMouseOut={(e) => {
          if (!isExpanded) e.currentTarget.style.backgroundColor = 'white';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FaDownload size={18} color="#303C49" />
          <ManText variant="heading-s" as="h3" style={{ margin: 0, color: '#000' }}>
            Download Ride Analysis Data
          </ManText>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {vin_short && vin_short.length > 0 && !isExpanded && (
            <span
              style={{
                fontSize: '12px',
                color: '#666',
                backgroundColor: '#e8ebed',
                padding: '4px 8px',
                borderRadius: '12px',
              }}
            >
              {vin_short.length} vehicle{vin_short.length !== 1 ? 's' : ''} selected
            </span>
          )}
          <span
            style={{
              fontSize: '20px',
              color: '#666',
              transition: 'transform 0.2s',
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              display: 'inline-block',
            }}
          >
            ▼
          </span>
        </div>
      </div>
 
      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: '20px' }}>
          {/* ROW 1: Date Selection */}
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6'
          }}>
            <ManText variant="body-s" style={{ fontWeight: 'bold', marginBottom: '12px', display: 'block', color: '#495057' }}>
              Date Selection
            </ManText>
           
            {/* Date Selection Layout - Side by Side */}
            <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
             
              {/* Left Side: Date Pickers */}
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <ManText variant="body-s" style={{ fontWeight: '600', color: '#495057', fontSize: '12px' }}>
                    Start Date
                  </ManText>
                  <DatePicker
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    selectsStart
                    startDate={startDate}
                    endDate={endDate}
                    maxDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    customInput={<CustomDateInput placeholder="Select start date" />}
                    popperPlacement="bottom-start"
                  />
                </div>
 
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <ManText variant="body-s" style={{ fontWeight: '600', color: '#495057', fontSize: '12px' }}>
                    End Date
                  </ManText>
                  <DatePicker
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    selectsEnd
                    startDate={startDate}
                    endDate={endDate}
                    minDate={startDate}
                    maxDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    customInput={<CustomDateInput placeholder="Select end date" />}
                    popperPlacement="bottom-start"
                  />
                </div>
 
                {/* Date Range Info */}
                {daysDifference > 0 && (
                  <div
                    style={{
                      padding: '10px 14px',
                      backgroundColor: '#e8ebed',
                      color: '#303C49',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: '1px solid #c5cad0',
                      alignSelf: 'flex-end',
                    }}
                  >
                    📊 {daysDifference} day{daysDifference !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
 
              {/* Right Side: Quick Select Presets */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'flex-start' }}>
                <ManText variant="body-s" style={{ fontWeight: '600', color: '#495057', fontSize: '12px', marginBottom: '0px' }}>
                  Quick Select
                </ManText>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-end', height: '44px' }}>
                  {[
                    { label: 'Last 7 Days', days: 7 },
                    { label: 'Last 30 Days', days: 30 },
                    { label: 'Last 90 Days', days: 90 },
                    { label: 'Last 6 Months', days: 180 },
                    { label: 'Last Year', days: 365 },
                  ].map((preset) => (
                    <button
                      key={preset.days}
                      onClick={() => setDateRange(preset.days)}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: 'white',
                        color: '#495057',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500',
                        transition: 'all 0.2s',
                        height: 'fit-content',
                      }}
                      onMouseOver={(e) => {
                        e.target.style.backgroundColor = '#303C49';
                        e.target.style.color = 'white';
                        e.target.style.borderColor = '#303C49';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.backgroundColor = 'white';
                        e.target.style.color = '#495057';
                        e.target.style.borderColor = '#dee2e6';
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
 
          {/* ROW 2: Selected Vehicles */}
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6'
          }}>
            <ManText variant="body-s" style={{ fontWeight: 'bold', marginBottom: '10px', display: 'block', color: '#495057' }}>
              Selected Vehicles
            </ManText>
            {!vin_short || vin_short.length === 0 ? (
              <div
                style={{
                  padding: '10px 12px',
                  backgroundColor: '#fff3cd',
                  color: '#856404',
                  borderRadius: '4px',
                  fontSize: '13px',
                }}
              >
                ⚠️ Please select at least one vehicle from the filters to enable download
              </div>
            ) : (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <div
                    style={{
                      padding: '8px 12px',
                      backgroundColor: '#e8ebed',
                      color: '#303C49',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '600',
                      border: '1px solid #c5cad0',
                    }}
                  >
                    🚗 {vin_short.length} vehicle{vin_short.length !== 1 ? 's' : ''} selected
                  </div>
                  <button
                    onClick={() => setShowVehicles(!showVehicles)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'white',
                      color: '#303C49',
                      border: '1px solid #303C49',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                    }}
                    onMouseOver={(e) => {
                      e.target.style.backgroundColor = '#303C49';
                      e.target.style.color = 'white';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.backgroundColor = 'white';
                      e.target.style.color = '#303C49';
                    }}
                  >
                    {showVehicles ? 'Hide' : 'Show'} VINs
                  </button>
                </div>
                {showVehicles && (
                  <div
                    style={{
                      padding: '10px 12px',
                      backgroundColor: 'white',
                      color: '#495057',
                      borderRadius: '4px',
                      fontSize: '12px',
                      marginTop: '10px',
                      border: '1px solid #dee2e6',
                      maxHeight: '120px',
                      overflowY: 'auto',
                    }}
                  >
                    {vin_short.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>
 
          {/* ROW 3: Download Button */}
          <div style={{
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '6px',
            border: '1px solid #dee2e6',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}>
            <button
              onClick={handleDownload}
              disabled={isDownloading || !vin_short || vin_short.length === 0}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px 24px',
                backgroundColor: isDownloading || !vin_short || vin_short.length === 0 ? '#ccc' : '#303C49',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isDownloading || !vin_short || vin_short.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'all 0.2s',
                boxShadow: isDownloading || !vin_short || vin_short.length === 0 ? 'none' : '0 2px 4px rgba(48,60,73,0.2)',
              }}
              onMouseOver={(e) => {
                if (!isDownloading && vin_short && vin_short.length > 0) {
                  e.target.style.backgroundColor = '#1e2530';
                  e.target.style.boxShadow = '0 4px 8px rgba(48,60,73,0.3)';
                }
              }}
              onMouseOut={(e) => {
                if (!isDownloading && vin_short && vin_short.length > 0) {
                  e.target.style.backgroundColor = '#303C49';
                  e.target.style.boxShadow = '0 2px 4px rgba(48,60,73,0.2)';
                }
              }}
            >
              <FaDownload size={16} />
              {isDownloading ? 'Preparing Download...' : 'Generate Download Link (S3)'}
            </button>
           
            {error && (
              <div
                style={{
                  padding: '10px 12px',
                  backgroundColor: '#ffebee',
                  color: '#c62828',
                  borderRadius: '4px',
                  fontSize: '13px',
                  border: '1px solid #ef5350',
                }}
              >
                ❌ {error}
              </div>
            )}
 
            {downloadInfo && (
              <div
                style={{
                  padding: '15px',
                  backgroundColor: '#e8f5e8',
                  color: '#2e7d32',
                  borderRadius: '6px',
                  fontSize: '13px',
                  border: '1px solid #81c784',
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  ✅ Download Ready
                  <span style={{
                    fontSize: '11px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '10px'
                  }}>
                    {downloadInfo.file_size_mb} MB
                  </span>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  📄 <strong>{downloadInfo.filename}</strong>
                </div>
                <div style={{ marginBottom: '6px' }}>
                  📊 {downloadInfo.row_count.toLocaleString()} rows • {downloadInfo.vin_count} vehicle{downloadInfo.vin_count !== 1 ? 's' : ''} • {downloadInfo.date_range}
                </div>
                {downloadInfo.compressed && (
                  <div style={{ marginBottom: '6px' }}>
                    🗜️ Compressed: {downloadInfo.compression_ratio}% reduction (saved {downloadInfo.bandwidth_saved_kb} KB bandwidth)
                  </div>
                )}
                <div style={{ marginBottom: '8px', fontSize: '12px', color: '#558b2f' }}>
                  ⏰ Link expires: {new Date(downloadInfo.expires_at).toLocaleString()}
                  ({Math.round(downloadInfo.expires_in_seconds / 60)} minutes)
                </div>
                <div style={{ fontSize: '11px', color: '#666', fontStyle: 'italic', marginBottom: '8px' }}>
                  💡 File stored securely in Amazon S3 • Download started automatically
                </div>
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = downloadInfo.download_url;
                    link.download = downloadInfo.filename;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#4caf50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#388e3c';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#4caf50';
                  }}
                >
                  🔗 Download Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </ManCard>
  );
};
 
export default RideDataDownload;
 
 